// =========================================================
// Vojjin LOGGER Utilities
// =========================================================
import {registerGlobal} from "./core.js";

class Logger {

    static host = window.location.hostname;
    static isDevEnv =
        this.host.startsWith("local.") ||
        this.host.startsWith("dev.") ||
        this.host.startsWith("stage.");

    /**
     * Logs messages to the console if the current environment is set as development.
     * The function uses the spread operator to accept and log multiple arguments.
     *
     * This method is designed to streamline debugging by conditionally outputting
     * console log messages based on the `isDevEnv` property.
     *
     * @param {...*} args - The arguments to be logged in the console.
     */
    static log = (...args) => {
        if (this.isDevEnv) {
            console.log(...args);
        }
    }

    /**
     * Outputs a JavaScript object or other value to the console in a structured and navigable format
     * when running in a development environment.
     *
     * This function utilizes `console.dir` to display an interactive list of the object's properties
     * or any other value passed through the arguments. The output will only occur if the
     * `isDevEnv` property within the current context evaluates to true, ensuring it is used
     * exclusively in a development environment.
     *
     * @param {...*} args - The arguments to be logged to the console using `console.dir`.
     */
    static dir = (...args) => {
        if (this.isDevEnv) {
            console.dir(...args);
        }
    }

    /**
     * Logs warning messages to the console when in a development environment.
     *
     * This function checks if the application is running in a development
     * environment (via the `isDevEnv` property) before invoking `console.warn`.
     * It accepts one or more arguments to log as warning messages.
     *
     * @param {...*} args - The messages or data to be logged as warnings.
     */
    static warn = (...args) => {
        if (this.isDevEnv) {
            console.warn(...args);
        }
    }

    /**
     * Logs error messages to the console in a development environment.
     * This function is a no-op in non-development environments.
     *
     * @param {...*} args - One or more arguments to be logged as an error.
     */
    static error = (...args) => {
        if (this.isDevEnv) {
            console.error(...args);
        }
    }
}

registerGlobal("Logger", Logger);

